/**
 * The application's type stack, in one place.
 *
 * Both SPAs, `src/helpers` and the shared `common-app` tree read the family
 * from here rather than writing it out, so changing the app's font is one
 * edit rather than a sweep. `@common/styles/fonts` resolves from all of them.
 *
 * The weights live here too (`APP_FONT_WEIGHT_*`), in three tiers: medium,
 * semibold and bold. Nunito Sans draws about a step lighter than Roboto at
 * the same number, so medium and bold are CALIBRATED — they pick whatever
 * number reproduces the Roboto look on each face. Semibold is not: it holds
 * 700 on both, so Roboto is unchanged while Nunito deliberately runs a step
 * lighter. See `UiFontChoice.semiboldWeight` for why.
 *
 * The font itself is SELF-HOSTED: `@fontsource-variable/nunito-sans` is
 * imported once, beside the app's other font import, and bundled. That is the
 * whole reason this is viable now — an earlier pass at Nunito Sans (VE-24546)
 * was backed out because it meant injecting a fonts.googleapis.com stylesheet
 * at runtime, which fails silently on restricted-network deployments and is a
 * CSP and privacy liability. Loading it from npm has none of those problems,
 * and the repo already did exactly this for Roboto.
 *
 * Keep it that way: nothing here should ever resolve to a remote stylesheet.
 */

/**
 * The app-wide sans stack. Matches the design system's own — DSM defines
 * `--dsm-typography-family-sans` as `"Nunito Sans", ui-sans-serif, system-ui,
 * sans-serif` in the vpe-design repo, under each scope's generated
 * `tokens.css`, not in this one — so the value is copied, not imported. This
 * stack is that one with the variable package's own family name prepended.
 *
 * BOTH family names are listed on purpose. `@fontsource-variable/*` registers
 * its `@font-face` as "Nunito Sans **Variable**", while the design system and
 * the static `@fontsource/nunito-sans` package both call it "Nunito Sans".
 * Naming only one means the stack silently falls through to the system sans
 * if the package is ever swapped — which is precisely how the app ended up
 * with a scattering of dead `fontFamily: 'Nunito'` declarations that have
 * never rendered anything.
 *
 * The fallbacks matter more than they look: they are what renders in the gap
 * before the webfont loads, so they should be a system sans rather than a
 * serif default.
 */
export const APP_FONT_FAMILY =
  '"Nunito Sans Variable", "Nunito Sans", ui-sans-serif, system-ui, sans-serif';

/**
 * The faces the app can be put on. The `useRobotoFont` config picks between
 * the first and the last; the middle one exists for the dev dropdown. Via the
 * application config.
 *
 * Deliberately a SHORT CLOSED LIST rather than a free-form family string.
 * Every option here has to be bundled, and a typo in a free-form value would
 * fail the way missing fonts always do — silently, by rendering something
 * else. A closed list makes an unknown value fall back to the default
 * instead.
 */
export type UiFontId = 'nunitoSans' | 'nunitoSans95' | 'roboto';

export interface UiFontChoice {
  readonly id: UiFontId;
  readonly label: string;
  readonly family: string;
  /**
   * Width-axis setting. Nunito Sans Variable carries `wdth` 75%-125%, so it
   * can be condensed without changing typeface. Requires the `wdth` build of
   * the font, which is what App imports.
   */
  readonly stretch?: string;
  /**
   * Titles, buttons, tabs and banners — MUI's `button` and `h6` variants take
   * it, so it reaches far past the sites that name it.
   *
   * ON NUNITO IT IS 600, which is NOT the optical match for Roboto 500: that
   * would be ~700 (measured stem widths put Nunito 700 at Roboto 500, and
   * Nunito 500 at Roboto 400). 700 read too heavy across the editor — warning
   * banners, tabs, toolbar dropdowns — so this tier deliberately sits a half
   * step below what shipped, between Roboto 400 and 500.
   *
   * That also keeps the three tiers distinguishable on Nunito (600 / 700 /
   * 800) rather than letting medium and semibold collapse onto one value.
   */
  readonly mediumWeight: number;
  /**
   * The tier the app wrote as `600` — the heavier of the two weights Roboto
   * could actually render, used for section labels, badges, pills and column
   * headers.
   *
   * ON ROBOTO IT IS 700, i.e. unchanged: Roboto ships no 600 face, and CSS
   * resolves a requested 600 upward, so those sites always drew the 700 face.
   * An org on `useRobotoFont` sees exactly what it saw before any of this.
   *
   * ON NUNITO IT IS 700, a step BELOW `boldWeight`, and that is a deliberate
   * design choice rather than a calibration: Nunito at 700 reads lighter than
   * Roboto's bold did, which is the look the prototype asks for (the settings
   * shell specifies 18px/700 for its titles). Matching Roboto optically would
   * mean ~770, and that read too heavy at these sizes.
   *
   * So the tiers are not parallel across faces, on purpose. On Nunito this
   * shares a value with `mediumWeight`; on Roboto it shares one with
   * `boldWeight`. Each pair is distinguishable on the other face.
   */
  readonly semiboldWeight: number;
  /**
   * The app's heaviest tier. Nunito Sans draws lighter at every weight, so
   * 800 here against Roboto's 700.
   *
   * Measured stem widths put Roboto 700 between Nunito 770 and 800, so 800
   * runs about 7% heavier than Roboto bold did. That was left alone rather
   * than tuned to 770: at the sizes bold appears at — 14px table headers and
   * 18px dialog titles — the two differ by about a seventh of a pixel of
   * stem, which is below what anyone can see, and 800 keeps a legible step
   * above semibold instead of crowding it.
   */
  readonly boldWeight: number;
}

export const UI_FONT_CHOICES: readonly UiFontChoice[] = [
  {
    id: 'nunitoSans',
    label: 'Nunito Sans',
    family: APP_FONT_FAMILY,
    mediumWeight: 600,
    semiboldWeight: 700,
    boldWeight: 800,
  },
  {
    id: 'nunitoSans95',
    label: 'Nunito Sans, condensed',
    family: APP_FONT_FAMILY,
    stretch: '95%',
    mediumWeight: 600,
    semiboldWeight: 700,
    boldWeight: 800,
  },
  {
    id: 'roboto',
    label: 'Roboto',
    family: 'Roboto, Arial, Helvetica, sans-serif',
    mediumWeight: 500,
    semiboldWeight: 700,
    boldWeight: 700,
  },
];

/**
 * What a style should use when it needs the app's font.
 *
 * The CSS variable, NOT a literal — because the font is chosen at runtime by
 * the organization's `useRobotoFont` application config. A style that names a family
 * directly opts itself out of that choice and silently stays on whatever was
 * hardcoded, which is how 58 of these ended up pinned to Nunito Sans while
 * the rest of the app followed the config.
 *
 * The one exception is canvas: `ctx.font` cannot resolve a custom property,
 * and fails silently if given one. Those sites call `resolveAppFont` instead.
 */
export const APP_FONT = 'var(--app-font-family)';

/**
 * Title weight that follows the font, so a title keeps the weight it had on
 * Roboto. See `UiFontChoice.mediumWeight`.
 */
export const APP_FONT_WEIGHT_MEDIUM = 'var(--app-font-weight-medium)';

/**
 * The tier the app wrote as `600`: 700 on both faces, so Roboto is unchanged
 * and Nunito runs a step below bold. See `UiFontChoice.semiboldWeight`.
 */
export const APP_FONT_WEIGHT_SEMIBOLD = 'var(--app-font-weight-semibold)';

/** Bold that follows the font. See `UiFontChoice.boldWeight`. */
export const APP_FONT_WEIGHT_BOLD = 'var(--app-font-weight-bold)';

/**
 * What the app uses unless an organization opts out.
 *
 * ONE default, deliberately: absent config, `useRobotoFont: false`, and an
 * environment where that definition was never registered all land here, so
 * registering it is optional rather than a prerequisite. The earlier design
 * was a font-name config, which needed two defaults that did not agree and
 * was correspondingly hard to reason about.
 *
 * 95% width, not 100%, and that is a LAYOUT decision rather than a
 * typographic one. Measured at 13px against the real faces, Nunito Sans at
 * full width runs 1.5–6.8% wider than the Roboto this app's widths were all
 * sized against — about 5% on filenames, 6.8% on digits, its worst case. At
 * 95% the same strings land within roughly 2% of Roboto in either direction,
 * so the design's typeface arrives without re-tuning column budgets,
 * measurement slack and hardcoded widths that nobody tuned for it.
 *
 * The cost is that `ctx.font` cannot express a percentage width, so canvas
 * measures at 100% while the DOM renders 95% — see `resolveAppFont`. That
 * errs generous, which is the safe direction.
 */
export const DEFAULT_UI_FONT_ID: UiFontId = 'nunitoSans95';

/** What `useRobotoFont` selects when an organization does opt out. */
export const ROBOTO_UI_FONT_ID: UiFontId = 'roboto';

export const isUiFontId = (value: unknown): value is UiFontId =>
  typeof value === 'string' && UI_FONT_CHOICES.some((c) => c.id === value);

/**
 * Point the app at one of the choices.
 *
 * Writes the custom property every stylesheet and MUI theme reads, the
 * weights that go with it, and the width axis, which inherits from the root.
 * Canvas measuring sites do NOT follow a custom property — they call
 * `resolveAppFont` instead.
 */
export const applyUiFont = (id: UiFontId): void => {
  // Falls back to DEFAULT_UI_FONT_ID, not to UI_FONT_CHOICES[0]. Unreachable
  // today — every caller validates with `isUiFontId` first — but this design
  // already has two defaults that are easy to confuse, and a third one hidden
  // in the function that actually decides the font would be worse.
  const choice =
    UI_FONT_CHOICES.find((c) => c.id === id) ??
    UI_FONT_CHOICES.find((c) => c.id === DEFAULT_UI_FONT_ID);
  if (!choice || typeof document === 'undefined') {
    return;
  }
  const root = document.documentElement;
  root.style.setProperty('--app-font-family', choice.family);
  root.style.setProperty(
    '--app-font-weight-medium',
    String(choice.mediumWeight)
  );
  root.style.setProperty(
    '--app-font-weight-semibold',
    String(choice.semiboldWeight)
  );
  root.style.setProperty('--app-font-weight-bold', String(choice.boldWeight));
  root.style.fontStretch = choice.stretch ?? '100%';
};

const LAST_UI_FONT_KEY = 'redact.lastUiFont';

/**
 * What to paint with before the application configs arrive.
 *
 * The configs land after auth, well after first paint, so something has to
 * decide the first frame, and `DEFAULT_UI_FONT_ID` is the right guess for
 * almost everyone — it is what every org gets without opting out.
 *
 * The exception is an org that HAS opted out, which would otherwise see
 * Nunito Sans for a beat and then Roboto on every single load. So the last
 * resolved choice is remembered and replayed: the first load after a change
 * still swaps once, because nothing can know a value before it is fetched,
 * and every load after is correct from the first frame.
 */
export const applyBootFont = (): void => {
  let remembered: string | null = null;
  try {
    remembered = window.localStorage.getItem(LAST_UI_FONT_KEY);
  } catch {
    remembered = null;
  }
  applyUiFont(isUiFontId(remembered) ? remembered : DEFAULT_UI_FONT_ID);
};

/** Remember what the config resolved to, for the next load's first frame. */
export const rememberUiFont = (id: UiFontId): void => {
  try {
    window.localStorage.setItem(LAST_UI_FONT_KEY, id);
  } catch {
    // A forgotten preference only costs one extra swap next time.
  }
};

export const DEFAULT_ROW_FONT_SIZE_PX = 13;

/**
 * The folder view's row size as a number canvas can use.
 *
 * `folderTheme` states it as `var(--app-row-font-size, 13px)` so it can be
 * moved at runtime, and a custom property is not something `ctx.font`
 * accepts — it fails silently and keeps the previous font, which would leave
 * the Name column measuring at one size and rendering at another.
 */
export const resolveRowFontSize = (): string => {
  if (typeof document === 'undefined') {
    return `${DEFAULT_ROW_FONT_SIZE_PX}px`;
  }
  const resolved = getComputedStyle(document.documentElement)
    .getPropertyValue('--app-row-font-size')
    .trim();
  return resolved || `${DEFAULT_ROW_FONT_SIZE_PX}px`;
};

/**
 * The app font as a string canvas can actually use.
 *
 * Canvas does not resolve CSS custom properties, and — worse — it SILENTLY
 * IGNORES an invalid `ctx.font` assignment and keeps whatever font it had. So
 * a measuring site that reads a theme value of `var(--app-font-family)` would
 * quietly measure in the wrong face with no error anywhere. Read the computed
 * value instead, falling back to the literal.
 *
 * It returns the FAMILY ONLY — `ctx.font` has no way to express a percentage
 * width, so a canvas site measures `nunitoSans95` at its full 100% width while
 * the DOM renders it at 95%. That is the shipping default for every registered
 * org, so it is a production property, not a quirk of the dev tool. It errs
 * generous at all three measuring sites (filename column, filename popover,
 * overlay text), which is the safe direction: they reserve slightly more room
 * than the text needs rather than clipping it.
 */
export const resolveAppFont = (): string => {
  if (typeof document === 'undefined') {
    return APP_FONT_FAMILY;
  }
  const resolved = getComputedStyle(document.documentElement)
    .getPropertyValue('--app-font-family')
    .trim();
  return resolved || APP_FONT_FAMILY;
};

/**
 * Deliberately NOT the app font.
 *
 * Burned-in text overlays are rendered twice — once live into a canvas as the
 * user positions them, and once by the redaction pipeline when the output is
 * produced. Both have to agree, and overlays already saved were positioned
 * against Roboto's metrics. Re-fonting this would move text inside outputs
 * customers have already reviewed, so it stays pinned until the pipeline side
 * changes with it. See `src/helpers/textOverlay.ts`.
 */
export const BURNED_IN_TEXT_FONT_FAMILY = 'Roboto, "Open Sans", sans-serif';

/**
 * Ask the browser for the burned-in faces up front.
 *
 * Canvas `measureText` NEVER blocks on a webfont: if Roboto has not arrived it
 * silently measures the next family in the stack, whose advances differ by a
 * few percent. That matters here beyond appearance — the editor measures an
 * overlay to derive its box and PERSISTS that box to the asset, and the
 * burn-in engine positions text from the stored box. A measurement taken
 * against the wrong face is written into the output (the VE-30798 shape of
 * failure), and the editor looks correct throughout.
 *
 * Until this change `body` was Roboto, so the face was always requested at
 * first paint and this was safe by accident. `body` is now the app font and
 * nothing else on the dashboard asks for Roboto, so on a cold cache the first
 * measurement could run without it. Asking explicitly makes the pinning an
 * enforced property rather than a string-level one.
 */
export const preloadBurnedInFont = (): void => {
  void document.fonts?.load('400 16px Roboto');
  void document.fonts?.load('700 16px Roboto');
};
